import type { ProductSlugs } from '@deque/billing-utils';
import { Loader, Select, type SelectProps } from '@deque/cauldron-react';
import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo
} from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  useFormContext
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IMPACTS } from '~/app/types';
import type { SupportedIntegrationProductSlugs } from '~/common/constants';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { getIntegrationProductInfoFromSlug } from '~/common/utils/get-integration-product-name-from-slug';
import { useFieldsContext } from '../../TemplateWizardForm/IssueTypeFieldsContext';

type ControlledSelectProps = {
  name: string;
  control: Control<FieldValues>;
} & Omit<SelectProps, 'ref'>;

const ControlledSelect = ({
  name,
  control,
  ...otherSelectProps
}: ControlledSelectProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => <Select {...field} {...otherSelectProps} />}
    />
  );
};

const makeTitle = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

const emptyPriorityOption = {
  key: '',
  label: '',
  value: ''
};

export interface ImpactMappingProps {
  integrationProductSlug:
    | SupportedIntegrationProductSlugs
    | ProductSlugs.axeAuditorIntegration;
  setIsStepDataLoading: Dispatch<SetStateAction<boolean>>;
  setIsStepDataError: Dispatch<SetStateAction<boolean>>;
}

const ImpactMapping = ({
  integrationProductSlug,
  setIsStepDataLoading,
  setIsStepDataError
}: ImpactMappingProps) => {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const { control } = useFormContext();

  const { priorities, error, loading } = useFieldsContext();

  const allowedPriorities = priorities?.allowedValues;

  const prioritiesOptions = useMemo(
    () =>
      allowedPriorities
        ? [
            emptyPriorityOption,
            ...allowedPriorities.map(({ id, name }) => ({
              key: id,
              label: name,
              value: id
            }))
          ]
        : [{ ...emptyPriorityOption, label: t('No priorities available') }],
    [allowedPriorities, t]
  );

  useEffect(() => {
    setIsStepDataError(!!error);

    if (error) {
      setContents(
        t('There was an error fetching fields for this issue type.'),
        'error'
      );
    }
  }, [error]);

  useEffect(() => setIsStepDataLoading(loading), [loading]);

  const { displayName: integrationName } = getIntegrationProductInfoFromSlug(
    integrationProductSlug as unknown as ProductSlugs
  );

  if (loading) {
    return <Loader label={t('Loading issue priorities...')} />;
  }

  return (
    <div>
      {IMPACTS.map(field => (
        <ControlledSelect
          key={field}
          name={`impactMapping.${field}`}
          control={control}
          label={t('{{ integration }} Priority for {{impact}} Impact', {
            integration: integrationName,
            impact: makeTitle(field)
          })}
          options={prioritiesOptions}
          disabled={!priorities}
        />
      ))}
    </div>
  );
};

export default ImpactMapping;
